/**
 * UseMyPendingInstructorSignoffsQuery.
 *
 * Wraps GET ``/api/v1/training/pending-instructor-signoffs``. Sibling
 * of ``useMyPendingAttestationsQuery`` for INSTRUCTOR_SIGNOFF items in
 * PENDING — same caller-only filtering, same userId-keyed cache, same
 * 5-min ``staleTime`` cadence — same rationale as the secondary-
 * attestations hook: sidebar badge fires on every page mount, and
 * mutations already invalidate, so a longer stale window cuts
 * unnecessary refetches without affecting freshness in practice.
 */

import {useQuery} from '@tanstack/react-query';
import {useApiToken} from '@/matic/hooks/useApiClient';
import {useAppContext} from '@/matic/providers/AppProvider';
import {
  getMyTrainingApi,
  type PendingInstructorSignoff,
} from '../api/my-training-api';
import {myTrainingKeys} from '../query-keys';

export function useMyPendingInstructorSignoffsQuery() {
  const {userId} = useAppContext();
  const token = useApiToken();
  const enabled = Boolean(token && userId);
  return useQuery<PendingInstructorSignoff[]>({
    queryKey: myTrainingKeys.pendingInstructorSignoffs(userId ?? ''),
    async queryFn() {
      return getMyTrainingApi(token).listPendingInstructorSignoffs();
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}
